/**
 * Character Creation Wizard
 *
 * Multi-step wizard for creating player characters.
 * Up to 5 steps: Basics -> Class -> Abilities -> Combat -> Review. The class and
 * ability steps appear only when the active system has classes and abilities.
 */

import { useState, useCallback, useMemo } from 'react';
import { Icon, type IconName } from './Icon';
import { useCharacterStore } from '../stores/characterStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useDiceStore } from '../stores/diceStore';
import { CharacterImportModal } from './CharacterImportModal';
import type { AbilityScores, ClassDefinition, MagicLevel, TechLevel } from '@machina/core';
import { abilityStorageKey, getActiveSystem, getModifier } from '@machina/core';
import { activeCreationSpec } from '../lib/system-surface';
import {
  STEP_TITLES,
  classesFor,
  creationSteps,
  defaultClassOf,
  levelCapOf,
  proficiencyBonusFor,
  saveKeysOf,
  startingHealthFor,
  systemUsesArmorClass,
  systemUsesRaces,
} from '../lib/character-creation';

interface CharacterCreationWizardProps {
  onClose: () => void;
  onCreated?: (characterId: string) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

// =============================================================================
// STATIC DATA
// =============================================================================

// Race data with setting restrictions
interface RaceData {
  name: string;
  minTech?: TechLevel; // Requires at least this tech level
  minMagic?: MagicLevel; // Requires at least this magic level
}

const ALL_RACES: RaceData[] = [
  { name: 'Human' },
  { name: 'Elf' },
  { name: 'Dwarf' },
  { name: 'Halfling' },
  { name: 'Half-Elf' },
  { name: 'Half-Orc' },
  { name: 'Gnome' },
  { name: 'Tiefling', minMagic: 'medium' }, // Requires magic for planar ancestry
  { name: 'Dragonborn', minMagic: 'medium' },
  { name: 'Aasimar', minMagic: 'medium' },
  { name: 'Goliath' },
  { name: 'Warforged', minTech: 'industrial' }, // Requires tech for constructs
  { name: 'Genasi', minMagic: 'wild' },
  { name: 'Changeling', minMagic: 'medium' },
];

// Classes come from the active system (`adapter.getClasses`). Only their icons
// live here, because an icon is presentation and `ClassDefinition` has no slot
// for one. A class not in this map, such as a Doomsworn Role, gets the shield.
const CLASS_ICONS: Record<string, IconName> = {
  barbarian: 'attack',
  bard: 'horn',
  cleric: 'sparkle',
  druid: 'heart',
  fighter: 'sword',
  monk: 'star',
  paladin: 'shield',
  ranger: 'target',
  rogue: 'skull',
  sorcerer: 'crystal',
  warlock: 'moon',
  wizard: 'wand',
  artificer: 'sparkle',
};
const DEFAULT_CLASS_ICON: IconName = 'shield';

// Tech/Magic level ordering for filtering
const TECH_ORDER: TechLevel[] = ['bronze', 'medieval', 'renaissance', 'industrial', 'steampunk'];
const MAGIC_ORDER: MagicLevel[] = ['low', 'medium', 'wild'];

function techMeetsMin(current: TechLevel, min?: TechLevel): boolean {
  if (!min) return true;
  return TECH_ORDER.indexOf(current) >= TECH_ORDER.indexOf(min);
}

function magicMeetsMin(current: MagicLevel, min?: MagicLevel): boolean {
  if (!min) return true;
  return MAGIC_ORDER.indexOf(current) >= MAGIC_ORDER.indexOf(min);
}

// Filter races based on campaign profile. Classes are filtered by the system
// itself, since which classes fit a setting is that system's content.
function getAvailableRaces(techLevel: TechLevel, magicLevel: MagicLevel): RaceData[] {
  return ALL_RACES.filter(r =>
    techMeetsMin(techLevel, r.minTech) && magicMeetsMin(magicLevel, r.minMagic)
  );
}

/**
 * Abilities come from the active game system, not from a list in this file.
 *
 * 5e declares six and gets exactly the wizard it always had. Doomsworn declares
 * seven, and ATTUNEMENT — the casting stat for Pacts and the ceiling on rune
 * ratings — becomes something a player can actually enter. Hardcoding six here
 * was not a cosmetic gap: it meant every Reckoning resolved at +0 forever
 * because the score had nowhere to live (ISS-0128).
 */
type Ability = string;

// Quick presets
const PRESETS = [
  {
    id: 'warrior',
    name: 'Warrior',
    description: 'Strength-based fighter',
    race: 'Human',
    className: 'Fighter',
    abilities: { strength: 15, dexterity: 12, constitution: 14, intelligence: 8, wisdom: 13, charisma: 10 },
  },
  {
    id: 'mage',
    name: 'Mage',
    description: 'Intelligence-based wizard',
    race: 'Elf',
    className: 'Wizard',
    abilities: { strength: 8, dexterity: 14, constitution: 12, intelligence: 15, wisdom: 13, charisma: 10 },
  },
  {
    id: 'healer',
    name: 'Healer',
    description: 'Wisdom-based cleric',
    race: 'Dwarf',
    className: 'Cleric',
    abilities: { strength: 13, dexterity: 10, constitution: 14, intelligence: 8, wisdom: 15, charisma: 12 },
  },
  {
    id: 'sneak',
    name: 'Sneak',
    description: 'Dexterity-based rogue',
    race: 'Halfling',
    className: 'Rogue',
    abilities: { strength: 8, dexterity: 15, constitution: 12, intelligence: 13, wisdom: 10, charisma: 14 },
  },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CharacterCreationWizard({ onClose, onCreated }: CharacterCreationWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  // Get campaign profile for filtering
  const getCurrentCampaign = useCampaignStore((s) => s.getCurrentCampaign);
  const currentCampaign = getCurrentCampaign();
  const campaignProfile = currentCampaign?.settings?.profile;
  const techLevel = campaignProfile?.setting?.tech_level || 'medieval';
  const magicLevel = campaignProfile?.setting?.magic_level || 'medium';

  // The active system. Fixed for the life of the wizard, since it is chosen
  // when the campaign is entered.
  const adapter = useMemo(() => getActiveSystem(), []);
  const usesRaces = systemUsesRaces(adapter);
  const usesArmorClass = systemUsesArmorClass(adapter);

  // Races are still this file's list (see ALL_RACES), shown only when the
  // system has races at all. Classes come from the system, which also decides
  // which of them suit this campaign's tech and magic level.
  const availableRaces = usesRaces ? getAvailableRaces(techLevel, magicLevel) : [];
  const availableClasses = useMemo(
    () => classesFor(adapter, { techLevel, magicLevel }),
    [adapter, techLevel, magicLevel],
  );

  // Form state
  const [name, setName] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [race, setRace] = useState(usesRaces ? availableRaces[0]?.name || 'Human' : '');
  // Null for a classless system. Every read below guards it, and the stored
  // character then gets no class, level, hit dice or saves.
  const [selectedClass, setSelectedClass] = useState<ClassDefinition | null>(
    () => defaultClassOf(availableClasses),
  );
  const [level, setLevel] = useState(1);
  // A level only means something alongside a class.
  const characterLevel = selectedClass ? level : undefined;
  const [subclass, setSubclass] = useState('');
  // The active system's ability list, values to hand out, and default score.
  // Memoised on nothing: the system is fixed for the life of the wizard, since
  // it is chosen when the campaign is entered.
  const creationSpec = useMemo(() => activeCreationSpec(), []);
  const abilityKeys = useMemo(
    () => creationSpec.abilities.map(abilityStorageKey),
    [creationSpec],
  );
  const abilityLabels = useMemo(
    () =>
      Object.fromEntries(
        creationSpec.abilities.map(a => [abilityStorageKey(a), a.abbreviation]),
      ) as Record<Ability, string>,
    [creationSpec],
  );
  const unassigned = creationSpec.defaultScore;

  // Steps with nothing to choose are dropped: no class step for a classless
  // system, no ability step for one without abilities.
  const STEPS = useMemo(
    () => creationSteps({
      classCount: availableClasses.length,
      abilityCount: creationSpec.abilities.length,
    }),
    [availableClasses.length, creationSpec],
  );

  /** Every declared ability at the default score. */
  const blankAbilities = useCallback(
    () => Object.fromEntries(abilityKeys.map(k => [k, unassigned])) as AbilityScores,
    [abilityKeys, unassigned],
  );

  const [abilities, setAbilities] = useState<AbilityScores>(() =>
    Object.fromEntries(
      creationSpec.abilities.map(a => [abilityStorageKey(a), creationSpec.defaultScore]),
    ) as AbilityScores,
  );
  const [armorClass, setArmorClass] = useState(10);
  const [maxHp, setMaxHp] = useState(10);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Dice rolling state for ability scores
  const [abilityMethod, setAbilityMethod] = useState<'standard' | 'rolled'>('standard');
  // Rolled pool: array of { dice: number[], total: number } for assignment
  const [rolledPool, setRolledPool] = useState<{ dice: number[]; total: number }[]>([]);
  const [rerollsRemaining, setRerollsRemaining] = useState(3);
  const [isRolling, setIsRolling] = useState(false);

  // Dice store access
  const diceRoll = useDiceStore((s) => s.roll);
  const isDiceReady = useDiceStore((s) => s.isReady);

  // Store access
  const addCharacter = useCharacterStore((s) => s.addCharacter);
  const addToParty = useCharacterStore((s) => s.addToParty);

  // Undefined for a system without a proficiency bonus, or without levels.
  const proficiencyBonus = proficiencyBonusFor(adapter, characterLevel);

  // Starting HP is the system's call. Undefined means it does not set one from
  // these choices, and the player enters the number themselves.
  const suggestedHp = startingHealthFor(adapter, {
    abilityScores: abilities,
    classId: selectedClass?.id,
    level: characterLevel,
  });
  const hitDie = selectedClass?.hitDie;
  const saveKeys = saveKeysOf(selectedClass, adapter?.getAbilities() ?? []);

  // Suggested AC (10 + DEX mod). Still a d20 formula held here: only offered
  // when the system uses armour class and the character has a Dexterity.
  const suggestedAc = usesArmorClass && typeof abilities.dexterity === 'number'
    ? 10 + getModifier(abilities.dexterity)
    : undefined;

  // Generate 4d6 drop lowest (pure random, no animation)
  const generate4d6DropLowest = useCallback((): { dice: number[]; total: number } => {
    const dice = Array.from({ length: 4 }, () => Math.floor(Math.random() * 6) + 1);
    dice.sort((a, b) => b - a);
    const total = dice[0] + dice[1] + dice[2];
    return { dice, total };
  }, []);

  // Roll all ability scores - generates a pool of 6 values to assign
  const rollAllAbilities = useCallback(async () => {
    if (isRolling) return;
    setIsRolling(true);
    setAbilityMethod('rolled');

    // Roll 4d6 once with the 3D dice for drama
    if (isDiceReady) {
      await diceRoll('4d6');
    }

    // One roll per declared ability. A pool short of the ability count would
    // strand a stat at its default with no value left to assign it.
    const pool: { dice: number[]; total: number }[] = [];
    for (let i = 0; i < creationSpec.rolledPoolSize; i++) {
      pool.push(generate4d6DropLowest());
    }
    // Sort by total descending for easier assignment
    pool.sort((a, b) => b.total - a.total);

    setRolledPool(pool);
    // Reset abilities to defaults - player will assign from pool
    setAbilities(blankAbilities());
    setSelectedPreset(null);
    setIsRolling(false);
  }, [isRolling, isDiceReady, diceRoll, generate4d6DropLowest, creationSpec, blankAbilities]);

  // Reroll all abilities
  const rerollAbilities = useCallback(async () => {
    if (rerollsRemaining <= 0) return;
    setRerollsRemaining(prev => prev - 1);
    await rollAllAbilities();
  }, [rerollsRemaining, rollAllAbilities]);

  // Switch back to standard array
  const useStandardArray = useCallback(() => {
    setAbilityMethod('standard');
    setAbilities(blankAbilities());
    setRolledPool([]);
    setRerollsRemaining(3);
    setSelectedPreset(null);
  }, [blankAbilities]);

  // Apply preset (only if race/class are available in this campaign)
  const applyPreset = useCallback((preset: typeof PRESETS[0]) => {
    // Check if race is available
    const raceAvailable = availableRaces.some(r => r.name === preset.race);
    if (raceAvailable) {
      setRace(preset.race);
    }
    // Check if class is available
    const classData = availableClasses.find(c => c.name === preset.className);
    if (classData) {
      setSelectedClass(classData);
    }
    // Presets speak the six d20 abilities. Merge over the blank sheet so a
    // system ability the preset has never heard of keeps its default rather
    // than vanishing from the character.
    setAbilities({ ...blankAbilities(), ...preset.abilities });
    setSelectedPreset(preset.id);
  }, [availableRaces, availableClasses, blankAbilities]);

  // Navigation
  const nextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      // Auto-fill combat stats when entering the combat step, but only with
      // numbers the system actually gave. Otherwise the player's entry stands.
      if (STEPS[currentStep + 1] === 'combat') {
        if (suggestedHp !== undefined) setMaxHp(suggestedHp);
        if (suggestedAc !== undefined) setArmorClass(suggestedAc);
      }
      setCurrentStep(s => s + 1);
    }
  }, [currentStep, STEPS, suggestedHp, suggestedAc]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  // Submit
  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Character name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create the character
      // A classless character stores no class, level, hit dice or saves. The
      // record allows that (ISS-0143), and the accessors read it as "none".
      const character = addCharacter({
        name: name.trim(),
        playerName: playerName.trim() || undefined,
        race,
        classes: selectedClass
          ? [{
              name: selectedClass.name,
              level,
              subclass: subclass.trim() || undefined,
              // The stored class requires a hit die. 5e and Doomsworn always
              // declare one; a rules document may not, and 0 is the value no
              // die can have, so it cannot pass for a real one.
              hitDie: selectedClass.hitDie ?? 0,
            }]
          : [],
        abilityScores: abilities,
        proficiencyBonus,
        armorClass: usesArmorClass ? armorClass : undefined,
        maxHp,
        currentHp: maxHp,
        hitDice: selectedClass && hitDie ? `${level}d${hitDie}` : undefined,
        hitDiceRemaining: selectedClass && hitDie ? level : undefined,
        savingThrowProficiencies: saveKeys,
      });

      // Add to party
      addToParty(character.id);

      onCreated?.(character.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create character');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    const step = STEPS[currentStep];

    switch (step) {
      case 'basics':
        // Filter presets to only show those with available race+class
        const validPresets = PRESETS.filter(p =>
          availableRaces.some(r => r.name === p.race) &&
          availableClasses.some(c => c.name === p.className)
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Import Option */}
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              style={{
                padding: '10px 14px',
                background: 'rgba(30, 25, 40, 0.8)',
                border: '1px dashed rgba(212, 175, 55, 0.4)',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Icon name="plus" size={14} color="rgba(255,255,255,0.6)" />
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                Import existing character
              </span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                (JSON, PDF, or image)
              </span>
            </button>

            {/* Campaign Setting Info */}
            {(techLevel !== 'medieval' || magicLevel !== 'medium') && (
              <div style={{
                padding: 10,
                background: 'rgba(100, 80, 140, 0.15)',
                border: '1px solid rgba(100, 80, 140, 0.3)',
                borderRadius: 4,
                fontSize: 11,
                color: 'rgba(255,255,255,0.7)',
              }}>
                <strong style={{ color: '#a78bfa' }}>Campaign Setting:</strong>{' '}
                {techLevel.charAt(0).toUpperCase() + techLevel.slice(1)} era,{' '}
                {magicLevel === 'low' ? 'low magic' : magicLevel === 'wild' ? 'wild magic' : 'standard magic'}
                <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                  Some races/classes may be unavailable based on campaign settings.
                </div>
              </div>
            )}

            {/* Quick Presets */}
            {validPresets.length > 0 && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: GOLD }}>
                  Quick Presets
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {validPresets.map(preset => {
                    const isSelected = selectedPreset === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        style={{
                          padding: '10px 12px',
                          background: isSelected
                            ? 'rgba(212, 175, 55, 0.15)'
                            : 'rgba(30, 25, 40, 0.8)',
                          border: isSelected
                            ? `2px solid ${GOLD}`
                            : '1px solid rgba(212, 175, 55, 0.3)',
                          borderRadius: 4,
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                          {isSelected && <Icon name="check" size={14} color={GOLD} />}
                          <span style={{
                            fontSize: 13,
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? GOLD : '#fff',
                          }}>
                            {preset.name}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: isSelected ? 'rgba(212, 175, 55, 0.8)' : 'rgba(255,255,255,0.6)',
                          marginTop: 2,
                          marginLeft: isSelected ? 20 : 0,
                        }}>
                          {preset.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Show what the selected preset applied */}
                {selectedPreset && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    background: 'rgba(212, 175, 55, 0.1)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 4,
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.8)',
                  }}>
                    <Icon name="check" size={12} color="#4ade80" style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Applied: <strong style={{ color: GOLD }}>{[race, selectedClass?.name].filter(Boolean).join(' ')}</strong>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}> • Ability scores set</span>
                  </div>
                )}
              </div>
            )}

            {/* Character Name */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Character Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter character name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Player Name */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Player Name
              </label>
              <input
                type="text"
                value={playerName}
                onChange={e => setPlayerName(e.target.value)}
                placeholder="Who plays this character?"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Race, for systems that have races */}
            {usesRaces && (
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Race
              </label>
              <select
                value={race}
                onChange={e => setRace(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                {availableRaces.map(r => (
                  <option key={r.name} value={r.name}>{r.name}</option>
                ))}
              </select>
            </div>
            )}
          </div>
        );

      case 'class':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Class Selection Grid */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: GOLD }}>
                Class
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {availableClasses.map(cls => {
                  const isSelected = selectedClass?.id === cls.id;
                  return (
                  <button
                    key={cls.id}
                    type="button"
                    onClick={() => setSelectedClass(cls)}
                    style={{
                      padding: '12px 8px',
                      background: isSelected
                        ? 'rgba(212, 175, 55, 0.2)'
                        : 'rgba(30, 25, 40, 0.8)',
                      border: isSelected
                        ? `2px solid ${GOLD}`
                        : '1px solid rgba(212, 175, 55, 0.3)',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Icon
                      name={CLASS_ICONS[cls.id] ?? DEFAULT_CLASS_ICON}
                      size={20}
                      color={isSelected ? GOLD : 'rgba(255,255,255,0.7)'}
                    />
                    <span style={{
                      fontSize: 11,
                      color: isSelected ? GOLD : 'rgba(255,255,255,0.8)',
                      fontWeight: isSelected ? 600 : 400,
                    }}>
                      {cls.name}
                    </span>
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Level */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Level
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="range"
                  min={1}
                  max={levelCapOf(adapter)}
                  value={level}
                  onChange={e => setLevel(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{
                  width: 32,
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                  color: GOLD,
                }}>
                  {level}
                </span>
              </div>
            </div>

            {/* Subclass (optional) */}
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: GOLD }}>
                Subclass (optional)
              </label>
              <input
                type="text"
                value={subclass}
                onChange={e => setSubclass(e.target.value)}
                placeholder="e.g., Champion, Evocation, Circle of the Moon"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </div>

            {/* Class Info */}
            <div style={{
              padding: 12,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
            }}>
              {hitDie !== undefined && (
                <div>Hit Die: <strong style={{ color: GOLD }}>d{hitDie}</strong></div>
              )}
              {saveKeys.length > 0 && (
                <div>Saving Throws: <strong style={{ color: GOLD }}>{saveKeys.join(', ')}</strong></div>
              )}
              {proficiencyBonus !== undefined && (
                <div>Proficiency Bonus: <strong style={{ color: GOLD }}>+{proficiencyBonus}</strong></div>
              )}
            </div>
          </div>
        );

      case 'abilities': {
        // Get the values available for assignment based on method
        const availableValues = abilityMethod === 'standard'
          ? creationSpec.standardArray
          : rolledPool.map(r => r.total);

        // Track which values are already assigned
        const assignedValues = abilityKeys
          .map(k => abilities[k])
          .filter(v => v !== unassigned);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Method Toggle */}
            <div style={{
              display: 'flex',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={useStandardArray}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: abilityMethod === 'standard'
                    ? 'rgba(212, 175, 55, 0.2)'
                    : 'rgba(30, 25, 40, 0.8)',
                  border: abilityMethod === 'standard'
                    ? `2px solid ${GOLD}`
                    : '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: abilityMethod === 'standard' ? GOLD : 'rgba(255,255,255,0.8)',
                }}>
                  Standard Array
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  {creationSpec.standardArray.join(', ')}
                </div>
              </button>
              <button
                type="button"
                onClick={rollAllAbilities}
                disabled={isRolling}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: abilityMethod === 'rolled'
                    ? 'rgba(147, 51, 234, 0.2)'
                    : 'rgba(30, 25, 40, 0.8)',
                  border: abilityMethod === 'rolled'
                    ? '2px solid #a855f7'
                    : '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  cursor: isRolling ? 'wait' : 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: abilityMethod === 'rolled' ? '#a855f7' : 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}>
                  <Icon name="d20" size={14} color={abilityMethod === 'rolled' ? '#a855f7' : 'rgba(255,255,255,0.6)'} />
                  {isRolling ? 'Rolling...' : 'Roll Stats'}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  4d6 drop lowest
                </div>
              </button>
            </div>

            {/* Rolled Pool Display */}
            {abilityMethod === 'rolled' && rolledPool.length > 0 && !isRolling && (
              <div style={{
                padding: '10px 12px',
                background: 'rgba(147, 51, 234, 0.1)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                borderRadius: 4,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Your Rolled Stats
                  </div>
                  <button
                    type="button"
                    onClick={rerollAbilities}
                    disabled={rerollsRemaining <= 0 || isRolling}
                    style={{
                      padding: '4px 10px',
                      background: rerollsRemaining > 0 ? 'rgba(147, 51, 234, 0.3)' : 'rgba(50, 40, 60, 0.5)',
                      border: '1px solid rgba(147, 51, 234, 0.5)',
                      borderRadius: 4,
                      color: rerollsRemaining > 0 ? '#c084fc' : 'rgba(255,255,255,0.4)',
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: rerollsRemaining > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icon name="d20" size={10} />
                    Reroll ({rerollsRemaining})
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {rolledPool.map((roll, idx) => {
                    // Count how many times this value appears in the pool up to (and including) this index
                    const countInPoolUpToIdx = rolledPool.slice(0, idx + 1).filter(r => r.total === roll.total).length;
                    // Count how many abilities have been assigned this value
                    const countAssigned = assignedValues.filter(v => v === roll.total).length;
                    // This specific roll chip is "used" only if enough assignments exist to cover it
                    const isUsed = countInPoolUpToIdx <= countAssigned;
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: '6px 10px',
                          background: isUsed ? 'rgba(74, 222, 128, 0.15)' : 'rgba(30, 25, 40, 0.8)',
                          border: `1px solid ${isUsed ? 'rgba(74, 222, 128, 0.4)' : 'rgba(147, 51, 234, 0.4)'}`,
                          borderRadius: 4,
                          opacity: isUsed ? 0.6 : 1,
                        }}
                      >
                        {/* Mini dice display */}
                        <div style={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                          {roll.dice.map((d, i) => (
                            <span
                              key={i}
                              style={{
                                fontSize: 8,
                                color: i === 3 ? '#ef4444' : 'rgba(255,255,255,0.5)',
                                textDecoration: i === 3 ? 'line-through' : 'none',
                              }}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                        <div style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: isUsed ? '#4ade80' : '#c084fc',
                          textAlign: 'center',
                        }}>
                          {roll.total}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ability Score Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {abilityKeys.map(ability => {
                // For dropdowns, show available values plus current value plus unassigned placeholder
                const currentValue = abilities[ability];
                const dropdownOptions = [unassigned, ...availableValues].filter((val) => {
                  // Include the unassigned score, current value, and unused values
                  if (val === unassigned) return true;
                  if (val === currentValue) return true;
                  // Count how many times this value appears in availableValues
                  const availableCount = availableValues.filter(v => v === val).length;
                  // Count how many times it's assigned (excluding current ability)
                  const assignedCount = abilityKeys.filter(a => a !== ability && abilities[a] === val).length;
                  return assignedCount < availableCount;
                });
                // Remove duplicates and sort
                const uniqueOptions = [...new Set(dropdownOptions)].sort((a, b) => b - a);

                return (
                  <div key={ability}>
                    <label style={{
                      display: 'block',
                      marginBottom: 4,
                      fontSize: 11,
                      color: GOLD,
                      fontWeight: 500,
                    }}>
                      {abilityLabels[ability]}
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select
                        value={abilities[ability]}
                        onChange={e => setAbilities(prev => ({
                          ...prev,
                          [ability]: parseInt(e.target.value),
                        }))}
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          background: 'rgba(30, 25, 40, 0.8)',
                          border: `1px solid ${abilityMethod === 'rolled' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(212, 175, 55, 0.3)'}`,
                          borderRadius: 4,
                          color: abilities[ability] === unassigned ? 'rgba(255,255,255,0.4)' : '#fff',
                          fontSize: 14,
                        }}
                      >
                        {uniqueOptions.map(val => (
                          <option key={val} value={val}>
                            {val === unassigned ? '—' : val}
                          </option>
                        ))}
                      </select>
                      <span style={{
                        width: 36,
                        textAlign: 'center',
                        fontSize: 13,
                        fontWeight: 500,
                        color: abilities[ability] === unassigned
                          ? 'rgba(255,255,255,0.3)'
                          : getModifier(abilities[ability]) >= 0
                            ? 'rgba(134, 239, 172, 0.9)'
                            : 'rgba(248, 113, 113, 0.9)',
                      }}>
                        {abilities[ability] === unassigned ? '—' : (
                          (getModifier(abilities[ability]) >= 0 ? '+' : '') + getModifier(abilities[ability])
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* What this system's extra abilities cost you */}
            {creationSpec.note && (
              <div style={{
                padding: 10,
                background: 'rgba(147, 51, 234, 0.08)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                borderRadius: 4,
                fontSize: 11,
                lineHeight: 1.5,
                color: 'rgba(216, 180, 254, 0.9)',
              }}>
                {creationSpec.note}
              </div>
            )}

            {/* Assignment progress */}
            <div style={{
              padding: 10,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.6)',
              textAlign: 'center',
            }}>
              {assignedValues.length < abilityKeys.length ? (
                <span>
                  Assigned: <strong style={{ color: GOLD }}>{assignedValues.length}/{abilityKeys.length}</strong>
                  <span style={{ marginLeft: 8, fontSize: 11 }}>
                    (select a value for each ability)
                  </span>
                </span>
              ) : (
                <span>
                  Total: <strong style={{ color: GOLD }}>
                    {abilityKeys.reduce((sum, key) => sum + abilities[key], 0)}
                  </strong>
                  <Icon name="check" size={14} color="#4ade80" style={{ marginLeft: 8, verticalAlign: 'middle' }} />
                </span>
              )}
            </div>
          </div>
        );
      }

      case 'combat':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* HP */}
            <div>
              <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: GOLD }}>
                <span>Max Hit Points</span>
                {suggestedHp !== undefined && (
                <button
                  type="button"
                  onClick={() => setMaxHp(suggestedHp)}
                  style={{
                    padding: '2px 8px',
                    background: 'rgba(212, 175, 55, 0.2)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 3,
                    color: GOLD,
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  Suggest: {suggestedHp}
                </button>
                )}
              </label>
              <input
                type="number"
                min={1}
                value={maxHp}
                onChange={e => setMaxHp(parseInt(e.target.value) || 1)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                {suggestedHp === undefined
                  ? 'This system does not set starting hit points from these choices. Enter what your table uses.'
                  : hitDie !== undefined && typeof abilities.constitution === 'number'
                    ? `Based on d${hitDie} + CON modifier (${getModifier(abilities.constitution) >= 0 ? '+' : ''}${getModifier(abilities.constitution)})`
                    : `Suggested by ${adapter?.name ?? 'the system'} rules`}
              </div>
            </div>

            {/* AC, for systems that have one */}
            {usesArmorClass && (
            <div>
              <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: GOLD }}>
                <span>Armor Class</span>
                {suggestedAc !== undefined && (
                <button
                  type="button"
                  onClick={() => setArmorClass(suggestedAc)}
                  style={{
                    padding: '2px 8px',
                    background: 'rgba(212, 175, 55, 0.2)',
                    border: `1px solid ${GOLD_DIM}`,
                    borderRadius: 3,
                    color: GOLD,
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  Suggest: {suggestedAc}
                </button>
                )}
              </label>
              <input
                type="number"
                min={1}
                value={armorClass}
                onChange={e => setArmorClass(parseInt(e.target.value) || 10)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(30, 25, 40, 0.8)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                Unarmored: 10 + DEX modifier. Adjust for armor/shield.
              </div>
            </div>
            )}

            {/* Speed (informational) */}
            <div style={{
              padding: 12,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
            }}>
              <div>Speed: <strong style={{ color: GOLD }}>30 ft.</strong></div>
              {hitDie !== undefined && (
                <div>Hit Dice: <strong style={{ color: GOLD }}>{level}d{hitDie}</strong></div>
              )}
            </div>
          </div>
        );

      case 'review':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Character Summary */}
            <div style={{
              padding: 16,
              background: 'rgba(20, 15, 30, 0.8)',
              borderRadius: 4,
              border: `1px solid ${GOLD_DIM}`,
            }}>
              <h3 style={{ fontSize: 18, marginBottom: 8, color: GOLD }}>{name || 'Unnamed Character'}</h3>
              {playerName && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                  Played by {playerName}
                </div>
              )}
              <div style={{ fontSize: 14, marginBottom: 4 }}>
                {[race, selectedClass?.name, characterLevel].filter(v => v !== undefined && v !== '').join(' ')}
                {selectedClass && subclass && <span style={{ color: 'rgba(255,255,255,0.6)' }}> ({subclass})</span>}
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${1 + (usesArmorClass ? 1 : 0) + (proficiencyBonus !== undefined ? 1 : 0)}, 1fr)`,
              gap: 8,
            }}>
              <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>HP</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{maxHp}</div>
              </div>
              {usesArmorClass && (
                <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>AC</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#60a5fa' }}>{armorClass}</div>
                </div>
              )}
              {proficiencyBonus !== undefined && (
                <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: GOLD, marginBottom: 2 }}>PROF</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#a78bfa' }}>+{proficiencyBonus}</div>
                </div>
              )}
            </div>

            {/* Abilities */}
            {abilityKeys.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(abilityKeys.length, 7)}, 1fr)`,
              gap: 4,
            }}>
              {abilityKeys.map(ability => (
                <div key={ability} style={{
                  padding: 6,
                  background: 'rgba(30, 25, 40, 0.6)',
                  borderRadius: 4,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 9, color: GOLD }}>{abilityLabels[ability]}</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{abilities[ability]}</div>
                  <div style={{
                    fontSize: 10,
                    color: getModifier(abilities[ability]) >= 0 ? '#86efac' : '#f87171',
                  }}>
                    {getModifier(abilities[ability]) >= 0 ? '+' : ''}{getModifier(abilities[ability])}
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Saves */}
            {saveKeys.length > 0 && (
            <div style={{
              padding: 10,
              background: 'rgba(20, 15, 30, 0.6)',
              borderRadius: 4,
              fontSize: 12,
            }}>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>Saving Throw Proficiencies: </span>
              <span style={{ color: GOLD }}>{saveKeys.join(', ')}</span>
            </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 440,
          maxHeight: '85vh',
          background: 'linear-gradient(180deg, #1a1520 0%, #0f0d12 100%)',
          border: `1px solid ${GOLD_DIM}`,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${GOLD_DIM}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ fontSize: 16, margin: 0, color: GOLD }}>Create Character</h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
              Step {currentStep + 1} of {STEPS.length}: {STEP_TITLES[STEPS[currentStep]]}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px' }}>
          {STEPS.map((_, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: index <= currentStep ? GOLD : 'rgba(255,255,255,0.1)',
                transition: 'background 0.3s',
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '8px 20px 20px',
          overflowY: 'auto',
        }}>
          {renderStepContent()}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '0 20px 12px',
            padding: 10,
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 4,
            fontSize: 12,
            color: '#ef4444',
          }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: `1px solid ${GOLD_DIM}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <button
            type="button"
            onClick={currentStep === 0 ? onClose : prevStep}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid rgba(255,255,255,0.2)`,
              borderRadius: 4,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={currentStep === 0 && !name.trim()}
              style={{
                padding: '8px 20px',
                background: GOLD,
                border: 'none',
                borderRadius: 4,
                color: '#000',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: currentStep === 0 && !name.trim() ? 0.5 : 1,
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !name.trim()}
              style={{
                padding: '8px 20px',
                background: '#22c55e',
                border: 'none',
                borderRadius: 4,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: isSubmitting || !name.trim() ? 0.5 : 1,
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Character'}
            </button>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <CharacterImportModal
          onClose={() => setShowImportModal(false)}
          onImported={(characterId) => {
            setShowImportModal(false);
            onCreated?.(characterId);
            onClose();
          }}
        />
      )}
    </div>
  );
}
